// 📄 components/Configuracion/ConfiguracionBadisZ.tsx
// Gestión de los BAdIs/objetos ABAP Z (desarrollos SAP personalizados)
// propios de ESTE proyecto — mismo patrón estructural que
// ConfiguracionTransaccionesZ.tsx (lista + modal alta/edición + ConfirmDialog
// para borrar), guardado DIRECTO contra Firestore (ver badiZService.ts).
import { useEffect, useState } from "react";
import { DatabaseZap, Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  getBadisZ, eliminarBadiZ, BadiZ
} from "../../services/badiZService.ts";
import { solucionBadiMeta } from "../../services/catalogoBadisService.ts";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import Alert from "../ui/Alert.tsx";
import BadiZFormModal from "./BadiZFormModal.tsx";

interface ConfiguracionBadisZProps {
  projectId: string;
  projectNombre?: string;
}

export default function ConfiguracionBadisZ({ projectId, projectNombre }: ConfiguracionBadisZProps) {
  // Borrar está restringido a Admin en firestore.rules (mismo patrón que
  // transaccionesZ) — se oculta el botón para el resto de los miembros.
  const { profile } = useAuth();
  const esAdmin = profile?.role === "admin";
  const [badis, setBadis] = useState<BadiZ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<BadiZ | null>(null);
  const [aEliminar, setAEliminar] = useState<BadiZ | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await getBadisZ(projectId);
      setBadis(data);
    } catch (err) {
      console.error("Error cargando los BAdIs Z:", err);
      setError("No se pudieron cargar los BAdIs Z de este proyecto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleGuardado = () => {
    const eraEdicion = !!editando;
    setModalOpen(false);
    setEditando(null);
    cargar();
    showAlert(eraEdicion ? "BAdI Z actualizado correctamente." : "BAdI Z guardado correctamente.");
  };

  const handleCancelarModal = () => {
    setModalOpen(false);
    setEditando(null);
  };

  const handleConfirmarEliminar = async () => {
    if (!aEliminar || eliminando) return;
    setEliminando(true);
    try {
      await eliminarBadiZ(projectId, aEliminar.id);
      setAEliminar(null);
      cargar();
      showAlert("BAdI Z eliminado correctamente.");
    } catch (err: any) {
      showAlert(err.message || "Error al eliminar el BAdI Z.", "error");
    } finally {
      setEliminando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando BAdIs Z...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">BAdIs Z</h3>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nuevo BAdI Z
        </button>
      </div>

      {badis.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <DatabaseZap className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Este proyecto todavía no tiene BAdIs Z propios.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {badis.map((b) => {
            const solMeta = solucionBadiMeta(b.solucion);
            return (
              <div key={b.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white truncate">{b.nombre}</span>
                  </div>
                  {(b.clase || b.interfaz) && (
                    <div className="text-xs font-mono text-gray-400 space-y-0.5 mt-1">
                      {b.clase && <p className="truncate"><span className="text-gray-500">Clase:</span> {b.clase}</p>}
                      {b.interfaz && <p className="truncate"><span className="text-gray-500">Interfaz:</span> {b.interfaz}</p>}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                      {solMeta.label}
                    </span>
                    {b.sistemasDisponibles && b.sistemasDisponibles.map((s) => (
                      <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                        {s}
                      </span>
                    ))}
                  </div>
                  {b.responsable && (
                    <p className="text-[11px] text-gray-500 mt-1.5">Responsable: <span className="text-gray-300">{b.responsable}</span></p>
                  )}
                </div>
                <button
                  onClick={() => setEditando(b)}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                  title="Editar"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {esAdmin && (
                  <button
                    onClick={() => setAEliminar(b)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(modalOpen || editando) && (
        <BadiZFormModal
          projectId={projectId}
          badiInicial={editando}
          onGuardado={handleGuardado}
          onCancelar={handleCancelarModal}
        />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este BAdI Z?"
          message={`"${aEliminar.nombre}" se eliminará de ${projectNombre || "este proyecto"}. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? "Eliminando..." : "Eliminar"}
          danger
          onConfirm={handleConfirmarEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
